import { Athlete } from "./athlete";
import { Company } from "./company";

export class User {

    id?: string;
    number?: number;
    firstName: string = '';
    lastName: string = '';
    email: string = '';
    phone?: string;
    documentNumber?: string;
    documentUrlFront?: string;
    documentUrlBack?: string;
    licenseDocumentUrl?: string;
    fiscalCodeDocumentUrl?: string;
    fiscalCodeFront?: string;
    fiscalCodeBack?: string;
    businessName?: string;
    fiscalCode?: string;
    vat?: string;
    sdi?: string;
    pec?: string;
    address?: string;
    addressNumber?: number;
    zip?: string;
    province?: string;
    city?: string;
    password?: string;
    image?: string;
    subtype?: string;
    acceptTerms?: string;
    acceptPrivacy?: string;
    acceptCompanyRules?: string;
    refreshToken?: string;
    recoveryNumber?: string;
    recoveryNumberDate?: string;
    lastAccess?: string;
    status?: string;
    createdAt?: string;
    updatedAt?: string;
    userGroupId?: string;
    companyId?: string;
    linkedCompanyId?: string;
    otp?: string;
    agentCommissionPercentFirstYear?: string;
    agentCommissionPercent?: string;
    referralCode?: string;
    fullName?: string;
    company?: Company;
    bank?: string;
    iban?: string;
    accountHolderPerson?: string;
    accountHolderCompany?: string;
    paymentPhone?: string;
    paymentEmail?: string;

    athletes?: Athlete[];

    /** Assegnazione categorie (es. creazione utente con ruolo mister) — inviato come array di id all’API. */
    categoryIds?: string[];
    /** Categorie collegate restituite dall’API in lettura (es. GET /users/:id). */
    categories?: { id: string; name?: string }[];

    constructor(raw?: Partial<User>) {
        if (!raw) {
            return;
        }

        this.id = raw.id;
        this.number = raw.number;
        this.password = raw.password;
        this.firstName = raw.firstName ?? '';
        this.lastName = raw.lastName ?? '';
        this.email = raw.email ?? '';
        this.phone = raw.phone ?? '';
        this.image = raw.image ?? '';
        this.address = raw.address ?? '';
        this.addressNumber = raw.addressNumber;
        this.documentNumber = raw.documentNumber ?? '';
        this.documentUrlFront = raw.documentUrlFront || (raw as {documentFront?: string}).documentFront || '';
        this.documentUrlBack = raw.documentUrlBack || (raw as {documentBack?: string}).documentBack || '';
        this.licenseDocumentUrl = raw.licenseDocumentUrl ?? '';
        this.fiscalCodeDocumentUrl = raw.fiscalCodeDocumentUrl
            || (raw as {fiscalCodeFront?: string; fiscalCodeUrlFront?: string}).fiscalCodeFront
            || (raw as {fiscalCodeUrlFront?: string}).fiscalCodeUrlFront
            || '';
        this.fiscalCodeFront = (raw as {fiscalCodeFront?: string; fiscalCodeUrlFront?: string}).fiscalCodeFront
            || (raw as {fiscalCodeUrlFront?: string}).fiscalCodeUrlFront
            || '';
        this.fiscalCodeBack = (raw as {fiscalCodeBack?: string; fiscalCodeUrlBack?: string}).fiscalCodeBack
            || (raw as {fiscalCodeUrlBack?: string}).fiscalCodeUrlBack
            || '';
        this.businessName = raw.businessName ?? '';
        this.fiscalCode = raw.fiscalCode ?? '';
        this.city = raw.city ?? '';
        this.province = raw.province ?? '';
        this.subtype = raw.subtype;
        this.acceptTerms = raw.acceptTerms;
        this.acceptPrivacy = raw.acceptPrivacy;
        this.acceptCompanyRules = raw.acceptCompanyRules;
        this.refreshToken = raw.refreshToken;
        this.recoveryNumber = raw.recoveryNumber;
        this.recoveryNumberDate = raw.recoveryNumberDate;
        this.lastAccess = raw.lastAccess;
        this.status = raw.status;
        this.createdAt = raw.createdAt;
        this.updatedAt = raw.updatedAt;
        this.otp = String(
            (raw as {otp?: string; OTP?: string; otpCode?: string; recoveryNumber?: string}).otp
            || (raw as {OTP?: string}).OTP
            || (raw as {otpCode?: string}).otpCode
            || raw.recoveryNumber
            || ''
        ).trim() || undefined;
        this.zip = raw.zip ?? '';
        this.sdi = raw.sdi ?? '';
        this.pec = raw.pec ?? '';
        this.vat = raw.vat ?? '';
        this.companyId = raw.companyId;
        this.linkedCompanyId = raw.linkedCompanyId;
        this.userGroupId = raw.userGroupId;
        this.agentCommissionPercentFirstYear = raw.agentCommissionPercentFirstYear ?? '';
        this.agentCommissionPercent = raw.agentCommissionPercent ?? '';
        this.referralCode = raw.referralCode ?? '';
        this.fullName = raw.fullName;
        this.bank = raw.bank ?? '';
        this.iban = raw.iban ?? '';
        this.accountHolderPerson = raw.accountHolderPerson ?? '';
        this.accountHolderCompany = raw.accountHolderCompany ?? '';
        this.paymentPhone = raw.paymentPhone ?? '';
        this.paymentEmail = raw.paymentEmail ?? '';

        this.athletes = raw.athletes;
        this.categoryIds = User.normalizeCategoryIds(raw);
        if (Array.isArray(raw.categories)) {
            this.categories = raw.categories
                .map((c: { id?: string; name?: string }) => ({
                    id: String(c?.id ?? ''),
                    name: c?.name,
                }))
                .filter((c) => !!c.id);
        }
        if (raw.company) {
            this.company = new Company(raw.company);
        }
    }

    /** Normalizza categoryIds da string[] o da oggetti { id } restituiti dall’API. */
    static normalizeCategoryIds(raw?: { categoryIds?: unknown; categories?: unknown }): string[] | undefined {
        const fromField = User.idsFromUnknownList(raw?.categoryIds);
        if (fromField.length > 0) {
            return fromField;
        }
        return User.idsFromUnknownList(raw?.categories);
    }

    private static idsFromUnknownList(value: unknown): string[] {
        if (!value) {
            return [];
        }
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                return User.idsFromUnknownList(parsed);
            } catch {
                return value.trim() ? [value.trim()] : [];
            }
        }
        if (!Array.isArray(value)) {
            return [];
        }
        return value
            .map((item) => {
                if (typeof item === 'string' || typeof item === 'number') {
                    return String(item).trim();
                }
                if (item && typeof item === 'object') {
                    const obj = item as { id?: unknown; categoryId?: unknown };
                    const id = obj.id ?? obj.categoryId;
                    return id != null ? String(id).trim() : '';
                }
                return '';
            })
            .filter((id) => !!id);
    }
}


